import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import authService from '../auth/authService';
import './Login.css';

/**
 * Login component for user authentication
 */
function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [googleClientId, setGoogleClientId] = useState('');
  const [googleConfigError, setGoogleConfigError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    let mounted = true;
    authService
      .getGoogleConfig()
      .then((config) => {
        if (!mounted) return;
        setGoogleClientId(config?.clientId || '');
        setGoogleConfigError('');
      })
      .catch((configError) => {
        if (!mounted) return;
        setGoogleClientId('');
        setGoogleConfigError(configError.message || 'Google Sign-In is unavailable');
      });

    return () => {
      mounted = false;
    };
  }, []);
  
  /**
   * Handle form submission
   * @param {Event} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await authService.login(username, password);
      navigate('/conversations'); // Redirect to conversations page after login
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setLoading(true);

    try {
      const idToken = credentialResponse?.credential;
      if (!idToken) {
        throw new Error('Google did not return a valid sign-in token.');
      }
      await authService.loginWithGoogle(idToken);
      navigate('/conversations');
    } catch (googleError) {
      console.error('Google sign-in error:', googleError);
      setError(googleError.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google sign-in failed. Please try again.');
  };
  
  return (
    <div className="login-container">
      <div className="login-card">
        <h2>Login</h2>
        {error && (
          <div className="error-message" role="alert" aria-live="assertive">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          
          <button type="submit" className="login-button" disabled={loading} aria-busy={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="auth-divider">
          <span>OR</span>
        </div>

        {googleClientId ? (
          <div className="google-login-container">
            <GoogleOAuthProvider clientId={googleClientId}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap={false}
              />
            </GoogleOAuthProvider>
          </div>
        ) : (
          <p className="google-login-unavailable">
            {googleConfigError || 'Google sign-in is currently unavailable.'}
          </p>
        )}
      </div>
    </div>
  );
}

export default Login;
